import { TimelineVariant } from '@/components/Timeline/allocation/types';
import { insideRangeCheck } from '@/utils/date';
import { useFluent } from 'fluent-vue';
import { DateTime } from 'luxon';
import { computed, Ref, toValue } from 'vue';

export function useEventAllocations(
    variantsRef: Ref<TimelineVariant[]>,
    visibleVariants: Ref<string[]>,
    timelineStart: Ref<DateTime | undefined>,
    timelineEnd: Ref<DateTime | undefined>,
) {
    const { $t } = useFluent();

    const filteredVariants = computed(
        () => toValue(variantsRef).filter((v) => toValue(visibleVariants).includes(v.id)) ?? [],
    );

    const referenceAllocationIds = computed(() => filteredVariants.value.flatMap((v) => v.referenceAllocationId ?? []));

    const filteredAllocations = computed(() => {
        return filteredVariants.value.flatMap((variant) => variant.allocations.nodes);
    });

    const referenceAllocations = computed(() => {
        return filteredAllocations.value
            .filter((a) => referenceAllocationIds.value.includes(a.id))
            .map((a) => ({
                ...a,
                id: `reference-${a.id}`,
                linkTarget: '',
                clickAction: { type: 'Jump-To-Allocation' as const },
            }));
    });

    const allocationsCount = computed(() => filteredAllocations.value.length);

    const referenceAllocationFirstColumnCell = computed(() => {
        const start = timelineStart.value;
        const end = timelineEnd.value;
        let allocationsInsideRange = referenceAllocations.value;
        if (start && end) {
            allocationsInsideRange = referenceAllocations.value.filter((a) =>
                insideRangeCheck(a.startDateTime, a.endDateTime, start, end),
            );
        }
        return {
            rowId: 'reference-allocation-row',
            allocationCount: {
                inRange: allocationsInsideRange.length,
                total: referenceAllocations.value.length,
            },
            label: $t('reference-allocations'),
            tooltip: $t('reference-allocations-tooltip'),
        };
    });

    return {
        filteredAllocations,
        allocationsCount,
        referenceAllocationFirstColumnCell,
        referenceAllocations,
    };
}
